import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { v4 } from 'uuid';
import {
    AnnotationFrameChange,
    AnnotationPlane,
    AnnotationRecord,
} from '~/domain/annotation/annotation';
import {
    euclideanDistanceMm,
    PointMm,
    toPointMm,
} from '~/domain/geometry/patientGeometry';
import { SummaryStatistics } from '~/domain/statistics/summaryStatistics';

/**
 * Smallest radius of a circle, and side of a rectangle, in mm. A click or a
 * resize closer than this is treated as a slip and refused, so a region can
 * never have zero area. It is below one screen pixel, so no region the user
 * can actually draw is refused.
 */
export const MIN_REGION_EXTENT_MM = 1e-3;

/**
 * A rectangle's corners in order around it, mm. Its sides run from the first
 * corner to the second and from the first to the fourth.
 */
export type RectangleCornersMm = readonly [PointMm, PointMm, PointMm, PointMm];

/** A circle on a plane. */
export interface CircleGeometry {
    readonly shape: 'circle';
    readonly centerMm: PointMm;
    readonly radiusMm: number;
}

/** A rectangle on a plane. */
export interface RectangleGeometry {
    readonly shape: 'rectangle';
    readonly cornersMm: RectangleCornersMm;
}

/** The outline of an intensity region. */
export type RegionGeometry = CircleGeometry | RectangleGeometry;

export type IntensityRegionShape = RegionGeometry['shape'];

/**
 * A placed region, with the statistics of the voxels inside it in the image's
 * native units. The region stays in the plane it was placed on, and is always
 * read in that plane, whatever the view is turned to later. `stats` is null
 * when there is no image data under the region; a zero is never invented.
 * The statistics are recomputed on every edit and cleared with the list, so
 * they never outlive the image they were read from.
 */
export type IntensityRegion = RegionGeometry &
    AnnotationRecord & { readonly stats: SummaryStatistics | null };

/** An in-progress placement; its outline follows the cursor until the second click. */
export type IntensityRegionDraft = RegionGeometry & {
    readonly plane: AnnotationPlane;
};

/**
 * `region`, placed or in progress, with its plane and outline moved by
 * `change`. Its size and statistics are kept: a rigid move changes neither.
 */
export function regionInFrame<T extends IntensityRegion | IntensityRegionDraft>(
    region: T,
    change: AnnotationFrameChange
): T {
    const plane = change.plane(region.plane);
    if (region.shape === 'circle') {
        return { ...region, plane, centerMm: change.point(region.centerMm) };
    }
    return {
        ...region,
        plane,
        cornersMm: mapCorners(region.cornersMm, change.point),
    };
}

/** `cornersMm` with each corner moved by `move`, in order. */
function mapCorners(
    cornersMm: RectangleCornersMm,
    move: (corner: PointMm) => PointMm
): RectangleCornersMm {
    const [a, b, c, d] = cornersMm;
    return [move(a), move(b), move(c), move(d)];
}

/**
 * The region of `shape` spanned from `anchorMm` to `cursorMm`, both on its
 * plane: a circle centred on the anchor through the cursor, or a rectangle
 * with the two at opposite corners and its sides along the in-plane unit
 * directions `u` and `v`.
 */
export function regionBetween(
    shape: IntensityRegionShape,
    anchorMm: PointMm,
    cursorMm: PointMm,
    u: ReadonlyVec3,
    v: ReadonlyVec3
): RegionGeometry {
    if (shape === 'circle') {
        return {
            shape,
            centerMm: anchorMm,
            radiusMm: euclideanDistanceMm(anchorMm, cursorMm),
        };
    }
    const diagonal = vec3.subtract(vec3.create(), cursorMm, anchorMm);
    const width = vec3.dot(diagonal, u);
    const height = vec3.dot(diagonal, v);
    const corner = (s: number, t: number) => {
        const point = vec3.scaleAndAdd(vec3.create(), anchorMm, u, s);
        return toPointMm(vec3.scaleAndAdd(point, point, v, t));
    };
    return {
        shape,
        cornersMm: [
            anchorMm,
            corner(width, 0),
            corner(width, height),
            corner(0, height),
        ],
    };
}

/**
 * Whether `region` is kept: a radius, or both sides, of at least
 * {@link MIN_REGION_EXTENT_MM}.
 */
export function regionHasArea(region: RegionGeometry): boolean {
    if (region.shape === 'circle') {
        return region.radiusMm >= MIN_REGION_EXTENT_MM;
    }
    const [a, b, , d] = region.cornersMm;
    return (
        euclideanDistanceMm(a, b) >= MIN_REGION_EXTENT_MM &&
        euclideanDistanceMm(a, d) >= MIN_REGION_EXTENT_MM
    );
}

/** `region` moved without turning, so that its centre lies at `centerMm`. */
export function regionCenteredAt<T extends RegionGeometry>(
    region: T,
    centerMm: PointMm
): T {
    const shift = vec3.subtract(
        vec3.create(),
        centerMm,
        regionCenterMm(region)
    );
    const moved = (point: PointMm) =>
        toPointMm(vec3.add(vec3.create(), point, shift));
    if (region.shape === 'circle') {
        return { ...region, centerMm };
    }
    return { ...region, cornersMm: mapCorners(region.cornersMm, moved) };
}

/** A rectangle's centre, the unit directions of its sides, and their lengths in mm. */
export function rectangleFrame(cornersMm: RectangleCornersMm) {
    const [a, b, c, d] = cornersMm;
    const u = vec3.subtract(vec3.create(), b, a);
    const v = vec3.subtract(vec3.create(), d, a);
    const widthMm = vec3.length(u);
    const heightMm = vec3.length(v);
    return {
        centerMm: toPointMm(vec3.lerp(vec3.create(), a, c, 0.5)),
        u: vec3.scale(u, u, 1 / widthMm),
        v: vec3.scale(v, v, 1 / heightMm),
        widthMm,
        heightMm,
    };
}

/** The centre of `region`, mm. */
export function regionCenterMm(region: RegionGeometry): PointMm {
    return region.shape === 'circle'
        ? region.centerMm
        : rectangleFrame(region.cornersMm).centerMm;
}

/** The area `region` encloses, mm². */
export function regionAreaMm2(region: RegionGeometry): number {
    if (region.shape === 'circle') {
        return Math.PI * region.radiusMm * region.radiusMm;
    }
    const { widthMm, heightMm } = rectangleFrame(region.cornersMm);
    return widthMm * heightMm;
}

/** Collision-free id. `uuid`, not `crypto`, which is banned repo-wide. */
export function createRegionId(shape: IntensityRegionShape): string {
    return `${shape}-${v4()}`;
}
